/**
 * FloorPlanSourceSection — where the client hands over the plan the product is
 * drawn from.
 *
 * This used to sit in the product drawer, above the variant picker, which put the
 * one input the product is MADE from in the one place that is meant to be about
 * choosing and pricing it. Everything else a floor plan needs — layout, design,
 * template, render type, logo — is collected here; the source file is
 * configuration too, and now lives with the rest of it.
 *
 * `FileUploadSection` is reused verbatim rather than reimplemented: it already
 * narrows the shared FileAPI scope to this product, filters out files another line
 * owns, and — critically — UNCLAIMS a filename before dropping its scope entry, so
 * a delete is not undone by `useResumedUploads` re-deriving the scope from the
 * attachments. `useSourceVisualsClaim` supplies the scope and makes the matching
 * claim when a file lands.
 *
 * The advisory keeps its original copy and key: the wording is about the plan
 * being uploaded, which has not changed, and reusing it keeps six locales out of
 * this change.
 */

import React from 'react'
import { Stack, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'

import { SystemMessage } from 'components/common/SystemMessage'
import { GRAY_900 } from 'constants/styling/theme'
import type { CartItemId, InputImagePolicyV2 } from 'models/purchaseFlowRevamp'

import { FLOOR_PLAN_SOURCE_FILE_TYPES } from '../../../components/productDrawer/uploadFileTypes'
import { FileUploadSection } from '../../../components/productDrawer/FileUploadSection.component'
import { STAGING_SENTINEL_PRODUCT_ID } from '../../../fileHub'
import { useSourceVisualsClaim } from '../../sourceVisuals/useSourceVisualsClaim'

interface Props {
  cartItemId: CartItemId
  /** Whether this product is produced FROM a client file at all. */
  requiresSource: boolean
  /** The selected variant's `inputImagePolicy`. */
  policy: InputImagePolicyV2 | undefined
}

export const FloorPlanSourceSection: React.FC<Props> = ({ cartItemId, requiresSource, policy }) => {
  const { t } = useTranslation(['purchase_flow_revamp'])
  const { scopeKey, variantId, claimedByOtherLines, claimedFilenames } = useSourceVisualsClaim(cartItemId, policy)

  if (!requiresSource) return null

  return (
    <Stack gap={1.2}>

      <Typography variant="text-lg" fontWeight={550} color={GRAY_900}>
        {t('purchase_flow_revamp:configuration.floor_plan_source_title')}
      </Typography>

      <FileUploadSection
        uploadProductId={STAGING_SENTINEL_PRODUCT_ID}
        productCode={variantId}
        scopeKey={scopeKey}
        claimedByOtherLines={claimedByOtherLines}
        ownerId={cartItemId}
        fileTypes={FLOOR_PLAN_SOURCE_FILE_TYPES}
      />

      <SystemMessage
        variant="info"
        message={t('purchase_flow_revamp:drawer.floor_plan_advisory')}
      />

      {/* The step's Proceed gate refuses this line without a plan; saying so here
          is what stops that refusal being a dead end the client cannot explain. */}
      {claimedFilenames.length === 0 &&
        <SystemMessage
          variant="warning"
          message={t('purchase_flow_revamp:configuration.floor_plan_source_required')}
        />
      }

    </Stack>
  )
}
